"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

export default function FreelancerRegisterPage() {
    const router = useRouter();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [form, setForm] = useState({
        email: "",
        password: "",
        honeypot: "", // скрытое поле для ботов
    });
    const [agreed, setAgreed] = useState(false);
    const [showPassword, setShowPassword] = useState(false); // ← добавлено для глазика

    // Таймер для защиты от ботов
    const [formStartTime, setFormStartTime] = useState<number | null>(null);
    const MIN_FILL_TIME_MS = 5000;

    useEffect(() => {
        setFormStartTime(Date.now());
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleRegister = async () => {
        setErrorMessage(null);

        if (form.honeypot.trim()) {
            setErrorMessage("Fehler: Bitte versuchen Sie es erneut.");
            return;
        }

        if (formStartTime) {
            const timeElapsed = Date.now() - formStartTime;
            if (timeElapsed < MIN_FILL_TIME_MS) {
                setErrorMessage("Bitte füllen Sie das Formular etwas langsamer aus.");
                return;
            }
        }

        if (!agreed) {
            setErrorMessage("Sie müssen den AGB zustimmen.");
            return;
        }

        if (!form.email.trim() || !form.password.trim()) {
            setErrorMessage("Bitte füllen Sie alle Felder aus.");
            return;
        }

        setLoading(true);

        try {
            const { data: existingProfile, error: checkError } = await supabase
                .from("profiles")
                .select("id")
                .eq("email", form.email.trim())
                .maybeSingle();

            if (checkError && checkError.code !== "PGRST116") {
                console.error("Ошибка проверки email:", checkError);
            }

            if (existingProfile) {
                setErrorMessage("Diese E-Mail-Adresse ist bereits registriert. Bitte loggen Sie sich ein.");
                setLoading(false);
                return;
            }

            const { data: authData, error: signUpError } = await supabase.auth.signUp({
                email: form.email.trim(),
                password: form.password,
                options: {
                    emailRedirectTo: `${window.location.origin}/auth/callback`,
                },
            });

            if (signUpError) throw signUpError;
            if (!authData.user) throw new Error("No user after signup");

            const user = authData.user;

            const { error: profileError } = await supabase
                .from("profiles")
                .upsert({
                    id: user.id,
                    email: form.email.trim(),
                    role: "freelancer",
                    has_onboarded: false,
                });

            if (profileError) {
                console.error("Ошибка при создании профиля:", profileError);
            }

            const { error: freelancerError } = await supabase
                .from("freelancers")
                .insert({
                    id: user.id,
                });

            if (freelancerError) {
                console.error("Ошибка при создании фрилансера:", freelancerError);
            }

            const { error: signInError } = await supabase.auth.signInWithPassword({
                email: form.email.trim(),
                password: form.password,
            });

            if (signInError) {
                console.warn("Автологин не удался:", signInError);
            }

            const { data: sessionData } = await supabase.auth.getSession();
            if (!sessionData.session) {
                await supabase.auth.refreshSession();
            }

            router.push("/freelancer-dashboard");
            router.refresh();
        } catch (err: any) {
            setErrorMessage(err.message || "Registrierung fehlgeschlagen");
            console.error("Общая ошибка:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
            <Card className="w-full max-w-lg shadow-2xl border border-gray-200 rounded-2xl">
                <CardHeader className="text-center pb-2">
                    <CardTitle className="text-3xl font-bold text-gray-800">Werde Freelancer</CardTitle>
                    <CardDescription className="mt-3 text-base text-gray-600">
                        Kostenlos registrieren – keine Provisionen – direkte Projekte
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pt-2">
                    {errorMessage && (
                        <Alert variant="destructive" className="rounded-xl">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{errorMessage}</AlertDescription>
                        </Alert>
                    )}

                    <div className="space-y-5">
                        <div>
                            <Label className="text-gray-700 font-medium">E-Mail *</Label>
                            <Input
                                name="email"
                                type="email"
                                placeholder="max@beispiel.de"
                                value={form.email}
                                onChange={handleChange}
                                disabled={loading}
                                className="mt-1.5 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>

                        <div className="relative">
                            <Label className="text-gray-700 font-medium">Passwort *</Label>
                            <Input
                                name="password"
                                type={showPassword ? "text" : "password"}
                                placeholder="Mindestens 8 Zeichen"
                                value={form.password}
                                onChange={handleChange}
                                disabled={loading}
                                className="mt-1.5 pr-11 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700 focus:outline-none transition-colors"
                                disabled={loading}
                            >
                                {showPassword ? (
                                    <EyeOff className="h-5 w-5" />
                                ) : (
                                    <Eye className="h-5 w-5" />
                                )}
                            </button>
                        </div>

                        {/* Honeypot — скрытое поле */}
                        <div style={{ display: "none" }}>
                            <Label>Website (nicht ausfüllen)</Label>
                            <Input
                                name="honeypot"
                                placeholder="Lassen Sie dieses Feld leer"
                                value={form.honeypot}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                checked={agreed}
                                onCheckedChange={(checked) => setAgreed(checked as boolean)}
                                disabled={loading}
                                className="border-gray-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                            />
                            <Label className="text-sm font-normal leading-snug text-gray-600">
                                Ich akzeptiere die{" "}
                                <a href="/agb" target="_blank" className="underline hover:text-blue-700">
                                    AGB
                                </a>
                                ,{" "}
                                <a href="/datenschutz" target="_blank" className="underline hover:text-blue-700">
                                    Datenschutz
                                </a>{" "}
                                und das{" "}
                                <a href="/impressum" target="_blank" className="underline hover:text-blue-700">
                                    Impressum
                                </a>
                                .
                            </Label>
                        </div>

                        <Button
                            onClick={handleRegister}
                            className="w-full h-12 text-base bg-gray-900 hover:bg-black text-white rounded-xl transition-all shadow-md hover:shadow-lg font-medium"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                    Registriere...
                                </>
                            ) : (
                                "Kostenlos registrieren"
                            )}
                        </Button>
                    </div>

                    <p className="text-center text-sm text-gray-600 mt-4">
                        Schon ein Konto?{" "}
                        <a href="/login" className="underline hover:text-blue-700 font-medium">
                            Einloggen
                        </a>
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}